import { useId } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { A11y, Navigation } from 'swiper/modules'
import { Props_SnippetBenefits } from './Benefits.props'
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import SwiperControls, { swiperNavigationOptions } from '../Shared/SwiperControls/SwiperControls'
import SnippetLink from '../Shared/Link/Link'

const SnippetBenefits = (props: Props_SnippetBenefits) => {
    const { snippet, heading, button, repeatable_benefitItems } = props

    const reactId = useId()
    const uniqueId = reactId.replace(/:/g, '')

    const classHeadingVisibility: string = !heading.visibility.value ? 'snippet-benefits__heading--hidden' : ''
    const backgroundImagePath = `/Blob/images/snippets/benefits/bg-${snippet.background?.value || 'grey'}.webp`

    const hasButton = Boolean(button.linkText?.value && button.linkHref?.value)

    const swiperProps = {
        className: 'snippet-benefits__swiper',
        modules: [Navigation, A11y],
        slidesPerView: 1,
        spaceBetween: 24,
        loop: true,
        breakpoints: {
            420: { slidesPerView: 1.15 },
            666: { slidesPerView: 2.25 },
            1200: { slidesPerView: 3.25 },
        },
        navigation: {
            ...swiperNavigationOptions,
            prevEl: `.swiper-controls__button--prev-${uniqueId}`,
            nextEl: `.swiper-controls__button--next-${uniqueId}`,
        },
    }

    const swiperControlsProps = {
        uniqueId,
        showNavigationMobile: true,
        showNavigationDesktop: true,
        showPaginationMobile: false,
        showPaginationDesktop: false,
        navigationPosition: 'sides' as const,
        navigationSidesPosition: 'outside' as const,
        className: 'snippet-benefits__navigation',
    }

    return (
        <section className="snippet-benefits">
            <img
                className="snippet-benefits__background-image"
                src={backgroundImagePath}
                alt=""
                aria-hidden="true"
                loading="lazy"
            />
            <div className="snippet-benefits__container">
                <h2 className={`snippet-benefits__heading ${classHeadingVisibility}`}>
                    {heading.content.value}
                </h2>

                <div className="snippet-benefits__carousel">
                    <Swiper {...swiperProps}>
                        {repeatable_benefitItems.map((item, index) => {
                            const { image, alt, heading: itemHeading, headingColor, content } = item
                            const itemHeadingColor = headingColor?.value || 'orange'
                            if (!image?.value || !itemHeading?.value) return null

                            return (
                                <SwiperSlide key={`snippet-benefits-${uniqueId}-${index}`} className="snippet-benefits__slide">
                                    <article className="snippet-benefits__card">
                                        <figure className="snippet-benefits__card-image-wrapper">
                                            <img
                                                className="snippet-benefits__card-image"
                                                src={image.value}
                                                alt={alt?.value || ''}
                                                loading="lazy"
                                            />
                                        </figure>
                                        <div className="snippet-benefits__card-body">
                                            <h3 className={`snippet-benefits__card-heading snippet-benefits__card-heading--${itemHeadingColor}`}>
                                                {itemHeading.value}
                                            </h3>
                                            {content?.value && (
                                                <div className="snippet-benefits__card-content">
                                                    {CompileMarkdown(content.value)}
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                </SwiperSlide>
                            )
                        })}
                    </Swiper>

                    <SwiperControls {...swiperControlsProps} />
                </div>

                {hasButton && (
                    <div className="snippet-benefits__button-wrapper">
                        <SnippetLink
                            url={button.linkHref.value}
                            linkStyles={button.linkStyle?.value}
                            openInNewTab={button.linkTarget?.value}
                            className="snippet-benefits__button"
                            aria-label={button.linkAriaLabel?.value || button.linkText.value}
                        >
                            {button.linkText.value}
                        </SnippetLink>
                    </div>
                )}
            </div>
        </section>
    )
}

export default SnippetBenefits
